import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Database, Plus, Pencil, Trash2, Save, X, RefreshCw, ChevronLeft, ChevronRight, Search, GripVertical } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';
import { adminManagementApi } from '../../services/api';
import StuiSelect from '../../components/common/StuiSelect';

const CATEGORIES = ['dataentry', 'engineering'];

/**
 * Administration > Security > Table Ownership
 *
 * CRUD UI for AppSec_TableOwner. Every project-DB table must be classified
 * with an owner permission key + category. Backed by the same registry
 * that DynamicController consults for authorization — adding a row here
 * makes the corresponding table usable via /api/dynamic/*.
 *
 * SysAdmin only (route-guarded in App.js).
 *
 * STUI-only: uses only classes established by DatabasesPage / OrganizationsPage
 * (stui-module-container, stui-list-panel, stui-detail-panel, etc.).
 * No inline CSS. No new styles. No edits to STUI.
 */
const TableOwnershipPage = () => {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);       // { TableName, OwnerKey, Category, Notes, isNew }
  const [dirtyFields, setDirtyFields] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [listCollapsed, setListCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const { notify } = useNotifications();

  useEffect(() => {
    const handleClickOutside = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminManagementApi.getTableOwners();
      setRows(res.data || []);
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to load table ownership registry');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const startAdd = () => {
    setSelected(null);
    setEditing({ TableName: '', OwnerKey: '', Category: 'dataentry', Notes: '', isNew: true });
    setDirtyFields(new Set(['TableName']));
  };

  const startEdit = () => {
    if (!selected) return;
    setEditing({ TableName: selected.tableName, OwnerKey: selected.ownerKey, Category: selected.category, Notes: selected.notes || '', isNew: false });
    setDirtyFields(new Set());
  };

  const cancelEdit = (force = false) => {
    if (!force && dirtyFields.size > 0) {
      notify.confirm('Discard unsaved changes?', () => { setEditing(null); setDirtyFields(new Set()); });
      return;
    }
    setEditing(null);
    setDirtyFields(new Set());
  };

  const handleField = (field, value) => {
    setEditing(prev => ({ ...prev, [field]: value }));
    const origMap = editing?.isNew
      ? { TableName: '', OwnerKey: '', Category: 'dataentry', Notes: '' }
      : { TableName: selected?.tableName, OwnerKey: selected?.ownerKey, Category: selected?.category, Notes: selected?.notes || '' };
    const newDirty = new Set(dirtyFields);
    if (value !== origMap[field]) newDirty.add(field); else newDirty.delete(field);
    setDirtyFields(newDirty);
  };

  const handleSave = async () => {
    if (!editing || dirtyFields.size === 0) return;
    if (!editing.TableName?.trim() || !editing.OwnerKey?.trim() || !editing.Category?.trim()) {
      notify.error('TableName, OwnerKey, and Category are required');
      return;
    }
    setSaving(true);
    try {
      if (editing.isNew) {
        await adminManagementApi.createTableOwner({
          TableName: editing.TableName.trim(),
          OwnerKey:  editing.OwnerKey.trim(),
          Category:  editing.Category,
          Notes:     editing.Notes || null
        });
      } else {
        await adminManagementApi.updateTableOwner(editing.TableName, {
          TableName: editing.TableName,
          OwnerKey:  editing.OwnerKey.trim(),
          Category:  editing.Category,
          Notes:     editing.Notes || null
        });
      }
      const savedName = editing.TableName;
      setEditing(null);
      setDirtyFields(new Set());
      const res = await adminManagementApi.getTableOwners();
      setRows(res.data || []);
      const refreshed = res.data?.find(r => r.tableName === savedName);
      if (refreshed) setSelected(refreshed);
    } catch (err) {
      notify.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!selected) return;
    notify.confirm(`Delete classification for '${selected.tableName}'? The table will be unreachable via DynamicController until reclassified.`, async () => {
      try {
        await adminManagementApi.deleteTableOwner(selected.tableName);
        setSelected(null);
        await load();
      } catch (err) {
        notify.error(err.response?.data?.error || 'Delete failed');
      }
    });
  };

  const isEditing = !!editing;
  const isNewRecord = !!editing?.isNew;
  const hasDirty = dirtyFields.size > 0;

  const filtered = searchTerm.trim()
    ? rows.filter(r => {
        const q = searchTerm.trim().toLowerCase();
        return (r.tableName || '').toLowerCase().includes(q)
            || (r.ownerKey  || '').toLowerCase().includes(q)
            || (r.category  || '').toLowerCase().includes(q);
      })
    : rows;

  if (loading) return <div className="stui-detail-empty"><Database size={48} /><p>Loading...</p></div>;

  return (
    <div className="stui-module-container">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <Database size={20} />
          <h1>Table Ownership</h1>
          <span className="stui-module-header-meta">{rows.length} tables</span>
        </div>
        <div className="stui-module-header-actions">
          {isEditing ? (
            <button
              className={`stui-crud-btn stui-delete-btn ${isNewRecord ? 'disabled' : ''}`}
              onClick={handleDelete}
              disabled={isNewRecord}
              title="Delete classification"
            >
              <Trash2 size={18} />
            </button>
          ) : (
            <button className="stui-crud-btn" onClick={startAdd} title="Classify a new table">
              <Plus size={18} />
            </button>
          )}
          <button
            className={`stui-crud-btn ${!isEditing && !selected ? 'disabled' : ''}`}
            onClick={isEditing ? () => cancelEdit() : startEdit}
            disabled={!selected && !isEditing}
            title={isEditing ? 'Cancel' : 'Edit'}
          >
            {isEditing ? <X size={18} /> : <Pencil size={18} />}
          </button>
          <button
            className={`stui-crud-btn ${!hasDirty ? 'disabled' : ''}`}
            onClick={handleSave}
            disabled={!hasDirty || saving}
            title="Save"
          >
            <Save size={18} />
          </button>
          <div className="stui-action-menu" ref={menuRef}>
            <button className="stui-action-menu-btn" onClick={() => setMenuOpen(!menuOpen)} title="Actions">
              <GripVertical size={16} />
            </button>
            {menuOpen && (
              <div className="stui-action-menu-dropdown">
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); load(); }}>
                  <RefreshCw size={16} /><span>Refresh</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="stui-list-view">
        {listCollapsed ? (
          <div className="stui-list-panel stui-collapsed">
            <button onClick={() => setListCollapsed(false)} className="stui-list-panel-expand-btn" title="Expand">
              <ChevronRight size={16} />
            </button>
          </div>
        ) : (
          <div className="stui-list-panel">
            <div className="stui-list-panel-header">
              <button onClick={() => setListCollapsed(true)} className="stui-list-panel-collapse-btn" title="Collapse">
                <ChevronLeft size={16} />
              </button>
              <div className="stui-search-wrapper">
                <Search size={14} className="stui-search-icon" />
                <input
                  className="stui-field-input"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Search tables..."
                />
              </div>
            </div>
            <div className="stui-list-scroll">
              {filtered.length === 0 ? (
                <div className="stui-list-empty">
                  {searchTerm ? 'No matches.' : 'No tables classified yet.'}
                </div>
              ) : (
                filtered.map(row => (
                  <button
                    key={row.tableName}
                    onClick={() => { setSelected(row); setEditing(null); setDirtyFields(new Set()); }}
                    className={`stui-list-item ${selected?.tableName === row.tableName ? 'selected' : ''}`}
                  >
                    <span className="stui-list-item-primary">{row.tableName}</span>
                    <span className="stui-list-item-secondary">{row.ownerKey} · {row.category}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        <div className="stui-detail-panel">
          {editing ? (
            <div className="stui-detail-content">
              <div className="stui-detail-grid">
                <div className="stui-col-24">
                  <div className="stui-detail-section-header">
                    {isNewRecord ? 'New Table Classification' : `Edit — ${editing.TableName}`}
                  </div>
                </div>
                <div className="stui-field-group stui-col-12">
                  <label>Table Name</label>
                  <input
                    className="stui-field-input"
                    value={editing.TableName}
                    onChange={e => handleField('TableName', e.target.value)}
                    disabled={!isNewRecord}
                    placeholder="e.g. Panels"
                    autoFocus={isNewRecord}
                  />
                </div>
                <div className="stui-field-group stui-col-12">
                  <label>Category</label>
                  <StuiSelect
                    className="stui-field-input"
                    value={editing.Category}
                    onChange={e => handleField('Category', e.target.value)}
                  >
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </StuiSelect>
                </div>
                <div className="stui-field-group stui-col-24">
                  <label>Owner Permission Key</label>
                  <input
                    className="stui-field-input"
                    value={editing.OwnerKey}
                    onChange={e => handleField('OwnerKey', e.target.value)}
                    placeholder="e.g. engineering.assets or module.subcontractor"
                  />
                </div>
                <div className="stui-field-group stui-col-24">
                  <label>Notes</label>
                  <input
                    className="stui-field-input"
                    value={editing.Notes}
                    onChange={e => handleField('Notes', e.target.value)}
                    placeholder="optional"
                  />
                </div>
              </div>
            </div>
          ) : selected ? (
            <div className="stui-detail-content">
              <div className="stui-detail-grid">
                <div className="stui-col-24">
                  <div className="stui-detail-section-header">{selected.tableName}</div>
                </div>
                <div className="stui-field-group stui-col-12">
                  <label>Owner Permission Key</label>
                  <input className="stui-field-input" value={selected.ownerKey} readOnly />
                </div>
                <div className="stui-field-group stui-col-12">
                  <label>Category</label>
                  <input className="stui-field-input" value={selected.category} readOnly />
                </div>
                <div className="stui-field-group stui-col-24">
                  <label>Notes</label>
                  <input className="stui-field-input" value={selected.notes || ''} readOnly />
                </div>
              </div>
            </div>
          ) : (
            <div className="stui-detail-empty">
              <Database size={48} />
              <p>Select a table to view its classification, or click + to add a new one.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TableOwnershipPage;
